import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStatistics, updateUser, updatePublicStats } from '../services/firestoreService';
import { useUsers } from '../hooks/useFirestore';
import LoadingSpinner from '../components/common/LoadingSpinner';
import TokenGeneratorModal from '../components/admin/TokenGeneratorModal';
import { initializeLucideIcons } from '../utils/initLucide';
import '../styles/admin.css';
import '../styles/admin-enhanced.css';

const AdminDashboard = () => {
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    approvedUsers: 0,
    pendingApprovals: 0,
    totalVIPTokens: 0,
    totalWinners: 0,
    totalPrizeMoney: 0
  });
  const [loading, setLoading] = useState(true);
  const { users, loading: usersLoading } = useUsers();
  const [processingUser, setProcessingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showTokenGenerator, setShowTokenGenerator] = useState(false);
  const [selectedTokenUser, setSelectedTokenUser] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [settings, setSettings] = useState({
    tokenPrice: 0,
    minTokens: 50,
    armyFundPercentage: 10,
    contactEmail: 'support@offerlifetime.com',
    whatsappNumber: '+91 8818001860'
  });

  useEffect(() => {
    loadStatistics();
    // Initialize Lucide icons
    initializeLucideIcons();
  }, []);

  useEffect(() => {
    if (!users || !Array.isArray(users)) return;

    const totalTokensSold = users.reduce((sum, user) => sum + (user.totalTokens || 0), 0);
    const totalRevenue = totalTokensSold * 0; // Tokens currently free
    const totalWinners = users.filter((user) => user.isWinner).length;
    const armyFundDonation = users.reduce((sum, user) => sum + (user.armyFundDonation || 0), 0);
    const approvedUsersCount = users.filter((user) => user.approvalStatus === 'approved').length;

    updatePublicStats({
      totalRegistrations: users.length,
      totalTokensSold,
      totalRevenue,
      totalWinners,
      armyFundDonation,
      approvedUsers: approvedUsersCount
    });
  }, [users]);

  useEffect(() => {
    // Re-initialize icons when section changes
    initializeLucideIcons();
  }, [activeSection]);

  const loadStatistics = async () => {
    const result = await getStatistics();
    if (result.success) {
      const data = result.data;
      setStats({
        totalUsers: data.totalUsers || 0,
        approvedUsers: data.approvedUsers || 0,
        pendingApprovals: data.pendingApprovals || 0,
        totalVIPTokens: data.totalVIPTokens || 0,
        totalWinners: data.totalWinners || 0,
        totalPrizeMoney: (data.totalWinners || 0) * 10000
      });
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login');
  };

  const handleApproveUser = async (userId) => {
    setProcessingUser(userId);
    const result = await updateUser(userId, { approvalStatus: 'approved' });
    if (result.success) {
      await loadStatistics();
    }
    setProcessingUser(null);
  };

  const handleRejectUser = async (userId) => {
    setProcessingUser(userId);
    const result = await updateUser(userId, { approvalStatus: 'rejected' });
    if (result.success) {
      await loadStatistics();
    }
    setProcessingUser(null);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      await updateUser(userToDelete.id, { status: 'deleted' });
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      await loadStatistics();
    }
  };

  const filteredUsers = users && Array.isArray(users) ? users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.phone?.includes(searchQuery)
  ) : [];

  const handleSaveSettings = async () => {
    alert('Settings saved successfully!');
  };

  if (loading) {
    return <LoadingSpinner text="Loading Admin Dashboard..." />;
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo">
            <h2>Offer Lifetime# Admin</h2>
          </div>
          <div className="admin-user-info">
            <span>{currentUser?.email}</span>
            <button onClick={handleLogout} className="btn-logout">
              <i data-lucide="log-out"></i> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="admin-layout">
        <aside className="admin-sidebar">
          <nav className="admin-nav">
            <button 
              className={`admin-nav-item ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              <i data-lucide="layout-dashboard"></i>
              <span>Dashboard</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'users' ? 'active' : ''}`}
              onClick={() => setActiveSection('users')}
            >
              <i data-lucide="users"></i>
              <span>Users Management</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'tokens' ? 'active' : ''}`}
              onClick={() => setActiveSection('tokens')}
            >
              <i data-lucide="ticket"></i>
              <span>VIP Tokens</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'draws' ? 'active' : ''}`}
              onClick={() => setActiveSection('draws')}
            >
              <i data-lucide="trophy"></i>
              <span>Prize Draws</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'winners' ? 'active' : ''}`}
              onClick={() => setActiveSection('winners')}
            >
              <i data-lucide="award"></i>
              <span>Winners</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveSection('payments')}
            >
              <i data-lucide="credit-card"></i>
              <span>Payments</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'inquiries' ? 'active' : ''}`}
              onClick={() => setActiveSection('inquiries')}
            >
              <i data-lucide="message-square"></i>
              <span>Inquiries</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveSection('settings')}
            >
              <i data-lucide="settings"></i>
              <span>Settings</span>
            </button>
          </nav>
        </aside>

        <main className="admin-main">
          {activeSection === 'overview' && (
            <div className="admin-content">
              <div className="admin-page-header">
                <h1>Dashboard Overview</h1>
                <p>Welcome back, {currentUser?.email}</p>
              </div>

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: '#667eea' }}>
                    <i data-lucide="users"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{stats.totalUsers}</h3>
                    <p>Total Users</p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ background: '#28a745' }}>
                    <i data-lucide="user-check"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{stats.approvedUsers}</h3>
                    <p>Approved Users</p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ background: '#ffc107' }}>
                    <i data-lucide="clock"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{stats.pendingApprovals}</h3>
                    <p>Pending Approvals</p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ background: '#17a2b8' }}>
                    <i data-lucide="ticket"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{stats.totalVIPTokens}</h3>
                    <p>VIP Tokens Issued</p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ background: '#dc3545' }}>
                    <i data-lucide="trophy"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{stats.totalWinners}</h3>
                    <p>Total Winners</p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ background: '#6f42c1' }}>
                    <i data-lucide="indian-rupee"></i>
                  </div>
                  <div className="stat-content">
                    <h3>₹{stats.totalPrizeMoney.toLocaleString()}</h3>
                    <p>Total Prize Money</p>
                  </div>
                </div>
              </div>

              <div className="admin-card" style={{ marginTop: '2rem' }}>
                <div className="admin-card-header">
                  <h2>Quick Actions</h2>
                </div>
                <div className="admin-card-body">
                  <div className="quick-actions">
                    <button className="action-btn" onClick={() => setActiveSection('users')}>
                      <i data-lucide="user-plus"></i>
                      <span>Manage Users</span>
                    </button>
                    <button className="action-btn" onClick={() => setActiveSection('tokens')}>
                      <i data-lucide="ticket"></i>
                      <span>Issue VIP Token</span>
                    </button>
                    <button className="action-btn" onClick={() => setActiveSection('draws')}>
                      <i data-lucide="trophy"></i>
                      <span>Create Prize Draw</span>
                    </button>
                    <button className="action-btn" onClick={() => setActiveSection('winners')}>
                      <i data-lucide="award"></i>
                      <span>Select Winners</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'users' && (
            <div className="admin-content">
              <div className="admin-page-header">
                <h1>User Management</h1>
                <p>Manage all registered users and their approval status</p>
              </div>

              {usersLoading ? (
                <LoadingSpinner text="Loading users..." />
              ) : (
                <div className="admin-card">
                  <div className="admin-card-header">
                    <h2>All Users ({filteredUsers.length})</h2>
                    <input
                      type="text"
                      placeholder="Search users by name, email, or phone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        width: '300px'
                      }}
                    />
                  </div>
                  <div className="admin-card-body">
                    <div className="table-responsive">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Registered</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.length === 0 ? (
                            <tr>
                              <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                                No users found
                              </td>
                            </tr>
                          ) : (
                            filteredUsers.map((user) => (
                            <tr key={user.id}>
                              <td>{user.name}</td>
                              <td>{user.email}</td>
                              <td>{user.phone}</td>
                              <td>
                                <span className={`status-badge status-${user.approvalStatus}`}>
                                  {user.approvalStatus}
                                </span>
                              </td>
                              <td>{user.createdAt?.toDate().toLocaleDateString()}</td>
                              <td>
                                <div className="action-buttons" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                  {user.approvalStatus === 'pending' && (
                                    <>
                                      <button 
                                        className="btn-approve"
                                        onClick={() => handleApproveUser(user.id)}
                                        disabled={processingUser === user.id}
                                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                                      >
                                        {processingUser === user.id ? 'Processing...' : 'Approve'}
                                      </button>
                                      <button 
                                        className="btn-reject"
                                        onClick={() => handleRejectUser(user.id)}
                                        disabled={processingUser === user.id}
                                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                                      >
                                        Reject
                                      </button>
                                    </>
                                  )}
                                  <button
                                    onClick={() => handleViewUser(user)}
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      background: '#667eea',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUser(user)}
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      background: '#dc3545',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'tokens' && (
            <div className="admin-content">
              <div className="admin-page-header">
                <h1>VIP Token Management</h1>
                <p>Approve users and issue VIP tokens</p>
              </div>
              
              <div className="admin-card" style={{ marginBottom: '2rem' }}>
                <div className="admin-card-header">
                  <h2>Pending User Approvals</h2>
                </div>
                <div className="admin-card-body">
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Address</th>
                          <th>Joined</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users && Array.isArray(users) && users.filter(u => u.approvalStatus === 'pending').map((user) => (
                          <tr key={user.id}>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.phone}</td>
                            <td>{user.address}, {user.city}</td>
                            <td>{user.createdAt?.toDate().toLocaleDateString()}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button 
                                  className="btn-approve"
                                  onClick={() => handleApproveUser(user.id)}
                                  disabled={processingUser === user.id}
                                >
                                  {processingUser === user.id ? 'Processing...' : 'Approve & Issue Token'}
                                </button>
                                <button 
                                  className="btn-reject"
                                  onClick={() => handleRejectUser(user.id)}
                                  disabled={processingUser === user.id}
                                >
                                  Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {users && Array.isArray(users) && users.filter(u => u.approvalStatus === 'pending').length === 0 && (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                              No pending approvals
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="admin-card">
                <div className="admin-card-header">
                  <h2>Approved Users - Ready for Token Issuance</h2>
                </div>
                <div className="admin-card-body">
                  <p style={{ marginBottom: '1rem', color: '#64748b' }}>Select an approved user to issue VIP token with QR code and certificate</p>
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Status</th>
                          <th>Tokens</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users && Array.isArray(users) && users.filter(u => u.approvalStatus === 'approved').map((user) => (
                          <tr key={user.id}>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.phone}</td>
                            <td><span className="status-badge status-approved">Approved</span></td>
                            <td>{user.vipTokenCount || 0}</td>
                            <td>
                              <button
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}
                                onClick={() => {
                                  setSelectedTokenUser(user);
                                  setShowTokenGenerator(true);
                                }}
                              >
                                <i data-lucide="file-badge" style={{ width: '16px', height: '16px' }}></i>
                                Generate VIP Token
                              </button>
                            </td>
                          </tr>
                        ))}
                        {users && Array.isArray(users) && users.filter(u => u.approvalStatus === 'approved').length === 0 && (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                              No approved users yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'draws' && (
            <div className="admin-content">
              <div className="admin-page-header">
                <h1>Prize Draw Management</h1>
                <p>Create and manage prize draws</p>
              </div>
              <div className="admin-card">
                <div className="admin-card-header">
                  <h2>All Prize Draws</h2>
                  <button
                    style={{
                      padding: '0.5rem 1.5rem',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                    onClick={() => alert('Create New Draw form will open here')}
                  >
                    + Create New Draw
                  </button>
                </div>
                <div className="admin-card-body">
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Prize Name</th>
                          <th>Value</th>
                          <th>Tokens Required</th>
                          <th>Draw Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                            No prize draws created yet. Click "Create New Draw" to start.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'winners' && (
            <div className="admin-content">
              <div className="admin-page-header">
                <h1>Winner Management</h1>
                <p>View and manage prize winners</p>
              </div>
              <div className="admin-card">
                <div className="admin-card-header">
                  <h2>All Winners</h2>
                </div>
                <div className="admin-card-body">
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Winner Name</th>
                          <th>Prize</th>
                          <th>Win Date</th>
                          <th>Prize Delivered</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                            No winners yet. Execute a prize draw to select winners.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'payments' && (
            <div className="admin-content">
              <div className="admin-page-header">
                <h1>Payment Transactions</h1>
                <p>View all payment transactions and revenue</p>
              </div>
              <div className="admin-card">
                <div className="admin-card-header">
                  <h2>All Transactions</h2>
                </div>
                <div className="admin-card-body">
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Transaction ID</th>
                          <th>User</th>
                          <th>Amount</th>
                          <th>Payment Method</th>
                          <th>Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                            No transactions yet. Payment transactions will appear here.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'inquiries' && (
            <div className="admin-content">
              <div className="admin-page-header">
                <h1>Contact Inquiries</h1>
                <p>View and respond to user inquiries</p>
              </div>
              <div className="admin-card">
                <div className="admin-card-header">
                  <h2>All Inquiries</h2>
                </div>
                <div className="admin-card-body">
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Subject</th>
                          <th>Message</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                            No inquiries yet. Inquiries from contact form will appear here.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'settings' && (
            <div className="admin-content">
              <div className="admin-page-header">
                <h1>Platform Settings</h1>
                <p>Configure platform settings and preferences</p>
              </div>
              <div className="admin-card">
                <div className="admin-card-header">
                  <h2>General Settings</h2>
                </div>
                <div className="admin-card-body">
                  <form onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }} style={{ display: 'grid', gap: '1.5rem', maxWidth: '600px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>VIP Token Price (₹)</label>
                      <input
                        type="number"
                        value={settings.tokenPrice}
                        onChange={(e) => setSettings({...settings, tokenPrice: e.target.value})}
                        style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Minimum Tokens for Draw</label>
                      <input
                        type="number"
                        value={settings.minTokens}
                        onChange={(e) => setSettings({...settings, minTokens: e.target.value})}
                        style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Army Fund Donation (% of Prize Money)</label>
                      <input
                        type="number"
                        value={settings.armyFundPercentage}
                        onChange={(e) => setSettings({...settings, armyFundPercentage: e.target.value})}
                        min="0"
                        max="100"
                        step="0.1"
                        style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
                      />
                      <small style={{ color: '#64748b', display: 'block', marginTop: '0.25rem' }}>Percentage of each prize money that will be donated to Army Fund</small>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Contact Email</label>
                      <input
                        type="email"
                        value={settings.contactEmail}
                        onChange={(e) => setSettings({...settings, contactEmail: e.target.value})}
                        style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>WhatsApp Number</label>
                      <input
                        type="tel"
                        value={settings.whatsappNumber}
                        onChange={(e) => setSettings({...settings, whatsappNumber: e.target.value})}
                        style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: '8px' }}
                      />
                    </div>
                    <button
                      type="submit"
                      style={{
                        padding: '0.875rem 2rem',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '1rem'
                      }}
                    >
                      Save Settings
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>User Details</h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <p><strong>Name:</strong> {selectedUser.name}</p>
              <p><strong>Email:</strong> {selectedUser.email}</p>
              <p><strong>Phone:</strong> {selectedUser.phone}</p>
              <p><strong>Father's Name:</strong> {selectedUser.fatherName}</p>
              <p><strong>Address:</strong> {selectedUser.address}</p>
              <p><strong>City:</strong> {selectedUser.city}</p>
              <p><strong>State:</strong> {selectedUser.state}</p>
              <p><strong>Pincode:</strong> {selectedUser.pincode}</p>
              <p><strong>Aadhaar:</strong> {selectedUser.aadhaarNumber}</p>
              <p><strong>PAN:</strong> {selectedUser.panNumber}</p>
              <p><strong>Status:</strong> <span className={`status-badge status-${selectedUser.approvalStatus}`}>{selectedUser.approvalStatus}</span></p>
              <p><strong>Registered:</strong> {selectedUser.createdAt?.toDate().toLocaleString()}</p>
            </div>
            <button
              onClick={() => setShowUserModal(false)}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 2rem',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h2 style={{ marginBottom: '1rem', color: '#dc3545' }}>Confirm Delete</h2>
            <p style={{ marginBottom: '1.5rem' }}>Are you sure you want to delete user <strong>{userToDelete.name}</strong>?</p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={confirmDelete}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Delete
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setUserToDelete(null); }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIP Token Generator Modal */}
      {showTokenGenerator && selectedTokenUser && (
        <TokenGeneratorModal
          user={selectedTokenUser}
          adminEmail={currentUser?.email}
          onClose={() => {
            setShowTokenGenerator(false);
            setSelectedTokenUser(null);
            initializeLucideIcons();
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
